export const THREE_DS2_AUTHENTICATION_EXCEPTION = 'ThreeDs2_Authentication_Exception'
export const ATTEMPT_EXCEED_LIMITATION = 'Attempt_Exceed_Limitation'
export const PAYMENT_ERROR = 'Payment_Error'
export const INVALID_TIMEOUT = 'Invalid_TimeOut'

const INVALID_SECURITY_CODE_MESSAGE = 'The CVV is invalid. Enter the 3- or 4-digit security code from your card.'
const EXPIRED_CARD_MESSAGE = 'This card has expired. Check the expiration date or use another card.'
const INSUFFICIENT_FUNDS_MESSAGE = 'This card has insufficient funds. Use another card or contact your card issuer.'
const PROCESSING_ERROR_MESSAGE = 'We could not process this card. Try again in a moment.'

type FieldError = {
  key: string
  message: string
}

const PROCESSOR_FIELD_ERRORS: Record<string, FieldError> = {
  invalid_cvc: {key: 'cardSecurityCode', message: INVALID_SECURITY_CODE_MESSAGE},
  incorrect_cvc: {key: 'cardSecurityCode', message: INVALID_SECURITY_CODE_MESSAGE},
  expired_card: {key: 'creditCardExpirationYear', message: EXPIRED_CARD_MESSAGE},
}

const PROCESSOR_PAGE_ERRORS: Record<string, string> = {
  insufficient_funds: INSUFFICIENT_FUNDS_MESSAGE,
  processing_error: PROCESSING_ERROR_MESSAGE,
}

export type PaymentPageErrorSource = 'callback' | 'error_handler'

export type PaymentPageErrorContext = {
  processor_response_reason: string
  processor_response_code: string
  processor_error_code?: string
  error_source: PaymentPageErrorSource
  card_setup: boolean
}

export type PaymentPageRefreshContext = {
  processor_response_code: string
  hpp_refresh_result: 'rendered' | 'error_response' | 'exception'
  hpp_refresh_strategy: 'cc_ref_retry' | 'standard_retry' | 'cc_ref_fallback'
}

type PaymentPageErrorMessages = {
  processor_declined_error: string
  attempts_exceeded_limit_error: string
  three_ds2_authentication_error?: string
  payment_page_token_refreshed_error?: string
}

type PaymentPageError = {
  key: string
  code: string
  message: string
  source: PaymentPageErrorSource
}

type PaymentPageErrorHandlerOptions = {
  container: HTMLElement
  allowCardSetupFallback: boolean
  refreshPaymentPage: (cardSetup: boolean) => Promise<boolean>
  reportError: (error: Error) => void
  sendErrorMessage: (key: string, message: string) => void
  trackError: (context: PaymentPageErrorContext) => void
  trackRefresh: (context: PaymentPageRefreshContext) => void
}

export type PaymentPageErrorHandler = (error: PaymentPageError) => Promise<void>

const MAX_CARD_SETUP_FAILURES = 2

function errorMessages(container: HTMLElement): PaymentPageErrorMessages {
  const value = container.getAttribute('data-error-messages')
  if (!value) throw new Error('Missing Zuora payment page error messages')
  return JSON.parse(value)
}

function normalizeErrorCode(code: string, message: string): string {
  if (
    code === THREE_DS2_AUTHENTICATION_EXCEPTION ||
    code === ATTEMPT_EXCEED_LIMITATION ||
    code === PAYMENT_ERROR ||
    code === INVALID_TIMEOUT
  ) {
    return code
  }
  return message.match(/\[(.*?)\]/)?.[1] || code
}

function processorErrorCode(message: string): string | undefined {
  const errorPath = message.match(/\[(card_error\/[^\]]+)\]/)?.[1]
  return errorPath?.split('/').pop()
}

function showPaymentError(container: HTMLElement, message: string): boolean {
  const banner = container.parentElement?.querySelector<HTMLElement>('.js-zuora-payment-error')
  const messageElement = banner?.querySelector<HTMLElement>('.js-zuora-payment-error-message')
  if (!banner || !messageElement) return false

  messageElement.textContent = message
  banner.hidden = false
  // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write
  banner.focus()
  return true
}

function hidePaymentError(container: HTMLElement): void {
  const banner = container.parentElement?.querySelector<HTMLElement>('.js-zuora-payment-error')
  const messageElement = banner?.querySelector<HTMLElement>('.js-zuora-payment-error-message')
  if (!banner || !messageElement) return

  messageElement.textContent = ''
  banner.hidden = true
}

function displayPageError(
  container: HTMLElement,
  sendErrorMessage: PaymentPageErrorHandlerOptions['sendErrorMessage'],
  message: string,
): void {
  if (!showPaymentError(container, message)) sendErrorMessage('error', message)
}

function paymentErrorMessage(messages: PaymentPageErrorMessages, errorCode: string, refreshRequired: boolean): string {
  if (refreshRequired && errorCode === THREE_DS2_AUTHENTICATION_EXCEPTION) {
    return messages.three_ds2_authentication_error || messages.processor_declined_error
  }
  if (refreshRequired && (errorCode === ATTEMPT_EXCEED_LIMITATION || errorCode === INVALID_TIMEOUT)) {
    return messages.payment_page_token_refreshed_error || messages.processor_declined_error
  }
  if (errorCode === ATTEMPT_EXCEED_LIMITATION) return messages.attempts_exceeded_limit_error
  return messages.processor_declined_error
}

function paymentPageRefreshRequired(errorCode: string, cardSetup: boolean): boolean {
  if (errorCode === INVALID_TIMEOUT) return true
  if (!cardSetup) return false

  return (
    errorCode === THREE_DS2_AUTHENTICATION_EXCEPTION ||
    errorCode === ATTEMPT_EXCEED_LIMITATION ||
    errorCode === PAYMENT_ERROR
  )
}

export function createPaymentPageErrorHandler({
  container,
  allowCardSetupFallback,
  refreshPaymentPage,
  reportError,
  sendErrorMessage,
  trackError,
  trackRefresh,
}: PaymentPageErrorHandlerOptions): PaymentPageErrorHandler {
  let currentRefresh: Promise<void> | undefined
  let cardSetupFailures = 0

  function sendFieldError(key: string, message: string): void {
    hidePaymentError(container)
    sendErrorMessage(key, message)
  }

  async function performRefresh(
    cardSetup: boolean,
    errorCode: string,
    strategy: PaymentPageRefreshContext['hpp_refresh_strategy'],
  ): Promise<void> {
    let result: PaymentPageRefreshContext['hpp_refresh_result']

    try {
      const rendered = await refreshPaymentPage(cardSetup)
      result = rendered ? 'rendered' : 'error_response'
      if (rendered) container.setAttribute('data-card-setup', String(cardSetup))
    } catch (error) {
      result = 'exception'
      reportError(error instanceof Error ? error : new Error(String(error)))
      const message = container.getAttribute('data-general-signature-error-message') || ''
      displayPageError(container, sendErrorMessage, message)
    } finally {
      currentRefresh = undefined
    }

    trackRefresh({
      processor_response_code: errorCode,
      hpp_refresh_result: result,
      hpp_refresh_strategy: strategy,
    })
  }

  async function refreshForError(cardSetup: boolean, errorCode: string): Promise<void> {
    if (cardSetup && errorCode !== INVALID_TIMEOUT) cardSetupFailures += 1

    const nextCardSetup = cardSetup && (!allowCardSetupFallback || cardSetupFailures < MAX_CARD_SETUP_FAILURES)
    const strategy = cardSetup ? (nextCardSetup ? 'cc_ref_retry' : 'cc_ref_fallback') : 'standard_retry'

    currentRefresh = performRefresh(nextCardSetup, errorCode, strategy)
    await currentRefresh
  }

  return async ({key, code, message, source}) => {
    if (key !== 'error') {
      sendFieldError(key, message)
      return
    }

    const errorCode = normalizeErrorCode(code, message)
    const processorCode = processorErrorCode(message)
    const fieldError = processorCode ? PROCESSOR_FIELD_ERRORS[processorCode] : undefined
    const cardSetup = container.getAttribute('data-card-setup') === 'true'
    const refreshRequired = paymentPageRefreshRequired(errorCode, cardSetup)

    if (refreshRequired && currentRefresh) {
      await currentRefresh
      return
    }

    trackError({
      processor_response_reason: message,
      processor_response_code: errorCode,
      ...(processorCode ? {processor_error_code: processorCode} : {}),
      error_source: source,
      card_setup: cardSetup,
    })

    if (fieldError) {
      hidePaymentError(container)
    } else {
      const messages = errorMessages(container)
      const displayMessage =
        (processorCode && PROCESSOR_PAGE_ERRORS[processorCode]) ||
        paymentErrorMessage(messages, errorCode, refreshRequired)
      displayPageError(container, sendErrorMessage, displayMessage)
    }

    if (refreshRequired) await refreshForError(cardSetup, errorCode)

    if (fieldError) sendFieldError(fieldError.key, fieldError.message)
  }
}
